/**
 * What the next second holds, as far as it can be known without touching the game (docs/spec/02 §1,
 * §3; 03 §2, §5): which ticks the hive moves on (Chrono), how the formation will sway and breathe,
 * who sits in which slot, where everything in the air is going, and what the Empress will do.
 */

import * as math from '../sim/math';
import * as sim from '../sim';
import type { GameState } from '../sim';
import * as horizon from './horizon';
import { Tracks } from './tracks';

// Local copies (see horizon.ts).
const { sin, cos, TAU } = math;
const { FIELD, FORMATION, NOVA } = sim;
const { HORIZON, MAX_ENEMIES, STRIDE } = horizon;

/** Formation rows -1 (a captive's place) to 4, ten columns each. */
const GRID = 60;
/** The Empress's drift, as the engine has it: how fast she swings and bobs, and by how much. */
const SWING_TICKS = 50;
const BOB_TICKS = 20;
const BOB = 6;
const PHASE_TWO_RATE = 1.4;

export class Forecast {
  /** `adv[k]`: how many times the hive has moved after `k` ticks (fewer than `k` under Chrono). */
  readonly adv = new Uint8Array(STRIDE);
  readonly sway = new Float32Array(STRIDE);
  readonly spread = new Float32Array(STRIDE);
  /** The distance between the formation's rows. */
  readonly pitch = new Float32Array(STRIDE);
  /** How many sit in the formation. */
  seated = 0;
  /** Who sits in each slot: an index into `state.enemies`, or -1. */
  readonly grid = new Int16Array(GRID);
  readonly tracks = new Tracks();
  /** A captive is about: shots must be checked against it. */
  captive = false;
  /** A captive is in the air, where a Nova would strike it. */
  captiveAloft = false;
  /** The column a captive sits at the top of, or is homing to; -1 for none. */
  captiveCol = -1;
  /** The Empress can be hit, and where she will be. */
  empress = false;
  readonly ex = new Float32Array(STRIDE);
  readonly ey = new Float32Array(STRIDE);
  /** Her lance burns while the hive's move count is between these (none when `to < from`). */
  lanceFrom = 0;
  lanceTo = -1;
  lanceX = 0;

  build(s: GameState): void {
    this.clock(s);
    this.formation(s);
    this.seats(s);
    this.tracks.build(s, this);
    this.theEmpress(s);
  }

  slotX(k: number, col: number): number {
    return FORMATION.cx + (col - 4.5) * FORMATION.cell * (this.spread[k] ?? 1) + (this.sway[k] ?? 0);
  }

  slotY(k: number, row: number): number {
    return FORMATION.top + row * (this.pitch[k] ?? FORMATION.cell);
  }

  /** The column whose slot is nearest `x` at tick `k` (it may be off either end). */
  column(k: number, x: number): number {
    return Math.round((x - FORMATION.cx - (this.sway[k] ?? 0)) / (FORMATION.cell * (this.spread[k] ?? 1)) + 4.5);
  }

  /** Who sits at (row, col), as an index into `state.enemies`; -1 for nobody. */
  seat(row: number, col: number): number {
    return col < 0 || col > 9 ? -1 : (this.grid[(row + 1) * 10 + col] ?? -1);
  }

  /** Chrono: the hive, its bombs and the Empress skip the odd ticks while it lasts (03 §2). */
  private clock(s: GameState): void {
    let moves = 0;
    this.adv[0] = 0;
    for (let k = 1; k <= HORIZON; k++) {
      const held = s.powers.chrono - (k - 1) > 0 && (s.tick + k) % 2 === 1;
      if (!held) moves++;
      this.adv[k] = moves;
    }
  }

  /** The engine's `updateFormation`, run ahead. */
  private formation(s: GameState): void {
    const f = s.formation;
    let t = f.t;
    let settledT = f.settledT;
    let sway = f.sway;
    let spread = f.spread;
    this.sway[0] = sway;
    this.spread[0] = spread;
    this.pitch[0] = FORMATION.cell * (1 + (spread - 1) / 2);
    for (let k = 1; k <= HORIZON; k++) {
      if (this.adv[k] !== this.adv[k - 1]) {
        t++;
        if (!f.settled) {
          const phase = (t / FORMATION.swayPeriod + 0.25) % 1;
          sway = FORMATION.swayAmp * (4 * Math.abs(phase - 0.5) - 1);
        } else {
          settledT++;
          if (Math.abs(sway) <= 0.5) sway = 0;
          else sway -= sway > 0 ? 0.5 : -0.5;
          spread = 1 + FORMATION.breatheAmp * (0.5 - 0.5 * cos((TAU * settledT) / FORMATION.breathePeriod));
        }
      }
      this.sway[k] = sway;
      this.spread[k] = spread;
      this.pitch[k] = FORMATION.cell * (1 + (spread - 1) / 2);
    }
  }

  private seats(s: GameState): void {
    this.grid.fill(-1);
    this.seated = 0;
    this.captive = false;
    this.captiveAloft = false;
    this.captiveCol = -1;
    const n = Math.min(s.enemies.length, MAX_ENEMIES);
    for (let i = 0; i < n; i++) {
      const e = s.enemies[i];
      if (!e || e.gone) continue;
      if (e.kind === 'captive') {
        this.captive = true;
        if (e.state === 'formed' || e.state === 'homing') this.captiveCol = e.col;
        if (e.state !== 'formed') this.captiveAloft = true;
      }
      if (e.state !== 'formed' && e.state !== 'morphing') continue;
      if (e.col < 0 || e.col > 9 || e.row < -1 || e.row > 4) continue;
      this.grid[(e.row + 1) * 10 + e.col] = i;
      this.seated++;
    }
  }

  /** Her drift, which stops while the lance is out, and when the lance will burn (03 §5). */
  private theEmpress(s: GameState): void {
    const em = s.empress;
    this.empress = em !== null && em.state === 'fighting';
    this.lanceTo = -1;
    if (!em || em.state !== 'fighting') return;
    const lancing = em.attack === 'lance';
    const whole = NOVA.lanceWarn + NOVA.lanceFire;
    // She is still from the move after the warning shows to the move the beam goes out.
    const stillFrom = em.lance === 'none' ? 2 : 1;
    const stillTo = lancing ? whole + 1 - em.attackT : 0;
    const rate = em.phase === 2 ? PHASE_TWO_RATE : 1;
    let drift = em.drift;
    let x = em.x;
    let y = em.y;
    this.ex[0] = x;
    this.ey[0] = y;
    for (let k = 1; k <= HORIZON; k++) {
      const u = this.adv[k] ?? 0;
      if (u !== this.adv[k - 1]) {
        if (u < stillFrom || u > stillTo) drift += rate;
        x = FIELD.w / 2 + NOVA.empressSwing * sin(drift / SWING_TICKS);
        y = NOVA.empressY + BOB * sin(drift / BOB_TICKS);
        // The lance is laid where she is when the warning shows.
        if (u === 1 && lancing && em.lance === 'none') this.lanceX = x;
      }
      this.ex[k] = x;
      this.ey[k] = y;
    }
    if (!lancing) return;
    if (em.lance !== 'none') this.lanceX = em.lanceX;
    this.lanceFrom = NOVA.lanceWarn + 1 - em.attackT;
    this.lanceTo = whole - em.attackT;
  }
}
